import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import {
  getUserFollowers, getUserFollowing,
  followUser, unfollowUser,
} from "../lib/services";
import { formatApiError } from "../lib/api";
import { lower } from "../lib/strings";

const MODE_LABEL = {
  followers: "followers",
  following: "following",
};

const EMPTY_LABEL = {
  followers: "no followers yet",
  following: "not following anyone yet",
};

// modal listing of followers OR following for a given username.
// each row links to the user's public profile + supports follow/unfollow inline.
export default function FollowList({ open, onClose, username, mode }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchFn = mode === "followers" ? getUserFollowers : getUserFollowing;

  const refresh = useCallback(async () => {
    if (!username || !open) return;
    setLoaded(false);
    try {
      const r = await fetchFn(username);
      setItems(r.items || []);
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setLoaded(true);
    }
  }, [username, open, fetchFn]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!open) return null;

  const onToggleFollow = async (item) => {
    if (item.isSelf) return;
    setBusyId(item.id);
    // optimistic toggle
    const next = !item.isFollowing;
    setItems((arr) =>
      arr.map((x) => (x.id === item.id ? { ...x, isFollowing: next } : x))
    );
    try {
      if (next) await followUser(item.username);
      else await unfollowUser(item.username);
    } catch (err) {
      // revert
      setItems((arr) =>
        arr.map((x) => (x.id === item.id ? { ...x, isFollowing: !next } : x))
      );
      toast(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid={`follow-list-${mode}`}
      className="fixed inset-0 z-[60] bg-black/80 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-black border-2 border-acid max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between px-5 pt-5 pb-3 border-b-2 border-white/15">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">
              @{username}
            </div>
            <div className="font-display text-2xl leading-tight mt-0.5">
              {MODE_LABEL[mode]}<span className="text-acid">.</span>
            </div>
          </div>
          <button
            data-testid={`follow-list-close-${mode}`}
            onClick={onClose}
            className="text-white/60 hover:text-white"
            aria-label="close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>

        <div className="overflow-y-auto flex-1">
          {!loaded ? (
            <p className="px-5 py-10 text-center font-mono text-xs text-white/40">loading...</p>
          ) : items.length === 0 ? (
            <p className="px-5 py-12 text-center font-mono text-xs text-white/40">
              {EMPTY_LABEL[mode]}.
            </p>
          ) : (
            <ul>
              {items.map((u) => (
                <li
                  key={u.id}
                  data-testid={`follow-row-${u.username}`}
                  className="relative border-b border-white/10 px-4 py-3 flex items-center gap-3"
                >
                  <Link
                    to={`/user/${u.username}`}
                    onClick={onClose}
                    data-testid={`follow-link-${u.username}`}
                    className="flex-1 min-w-0"
                  >
                    <div className="font-display text-base leading-tight truncate hover:text-acid transition-colors">
                      {lower(u.displayName) || lower(u.username) || "user"}
                    </div>
                    <div className="font-mono text-[10px] text-white/50 truncate">
                      @{u.username}
                    </div>
                  </Link>
                  {!u.isSelf && (
                    <button
                      data-testid={`follow-toggle-${u.username}`}
                      onClick={(e) => { e.stopPropagation(); onToggleFollow(u); }}
                      disabled={busyId === u.id}
                      className={`relative z-10 shrink-0 font-mono text-[10px] lowercase border-2 px-2.5 py-1.5 transition-colors flex items-center gap-1 disabled:opacity-40 ${
                        u.isFollowing
                          ? "border-acid bg-acid text-black"
                          : "border-acid text-acid hover:bg-acid hover:text-black"
                      }`}
                    >
                      {u.isFollowing ? (
                        <UserCheck className="w-3 h-3" strokeWidth={2} />
                      ) : (
                        <UserPlus className="w-3 h-3" strokeWidth={2} />
                      )}
                      {u.isFollowing ? "following" : "follow"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
